import { ApolloProvider } from '@apollo/client'
import { ConsoleLayout } from 'admin-web-ui'
import { flattenRoutes } from 'admin-web-utils'
import React, { useMemo } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { UserDropDown } from '../../components/UserDropDown'
import { useProjectRoutes } from '../../hooks/use-project-routes'
import { CONSOLE_ROOT_PATH, PROJECT_ROOT_PATH, buildRoutes } from '../../routes'
import { useAppContext } from '../App'
import { useConsoleContext } from './ConsoleContext'
import { getConsoleRoutes } from './routes'
/**
 * <Console> renders the main app navigation, and console routes deal
 * with customer-level functionality, such as user profile and management.
 */
export const Console: React.FC = () => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const projectRoutes = useProjectRoutes()
  const { cognitoUser } = useAppContext()
  const { me, navProject, customerClient } = useConsoleContext()

  const rootPaths = { project: PROJECT_ROOT_PATH, console: CONSOLE_ROOT_PATH }

  if (!cognitoUser) {
    throw new Error(
      'Cannot load Console container. Cognito user must be available.',
    )
  }

  /**
   * Flattens routes to make siblings routes in render
   */
  const consoleRouteElements = useMemo(() => {
    const routes = getConsoleRoutes()
    const flatRoutes = flattenRoutes([routes])
    const builtRoutes = buildRoutes({
      routes: flatRoutes,
      authGroups: me.authGroups,
      retryPath: pathname + search,
    })

    return builtRoutes
  }, [pathname, search, me.authGroups])

  return (
    <ApolloProvider client={customerClient}>
      <Routes>
        <Route
          element={
            <ConsoleLayout
              projects={me.projects}
              currentProject={navProject}
              routes={[getConsoleRoutes(), ...projectRoutes]}
              rootPaths={rootPaths}
              UserDropdown={UserDropDown}
              onProjectSwitch={(id) => navigate(`/console/project/${id}`)}
            />
          }
        >
          {consoleRouteElements}
        </Route>
      </Routes>
    </ApolloProvider>
  )
}
